<template>
  <div
    id="details"
    class="tab-pane block pb-12"
    role="tabpanel"
  >
    <div class="mt-12 mb-6 mb-print-0 pb-print-0 md:pb-3 pb-print-6 my-print-0">
      <TripPreamble :trip="trip" />
    </div>
    <div
      v-if="trip.tags?.length && !$isSol"
      class="inline-block px-0 mb-4 md:mb-4 pb-2 md:pb-0 -mt-1 print-hidden"
    >
      <nuxt-link
        v-for="tag in trip.tags"
        :key="tag.category_url"
        class="bg-beige hover:bg-light-gray float-left text-dark mr-2 mb-2 px-4 text-nowrap py-1 rounded-[1.25rem]"
        :to="tag.category_url"
        :data-translation="tag?.rubrik_translationkey"
      >
        {{ tag.rubrik }}
      </nuxt-link>
    </div>
    <div
      v-if="trip.special_videointroduction?.length"
      class="md:mt-4 w-full pb-4 print-hidden"
    >
      <LazyMediaLogoCard
        v-for="(videoIntroduction, idx) in specialVideoIntroductions"
        :key="idx"
        :logo="videoIntroduction.logo"
        :title="videoIntroduction.quote"
        :title-by="videoIntroduction.quote_by"
        :text="videoIntroduction.text"
        :media="videoIntroduction.media"
      />
    </div>
    <div
      v-if="hasMapPreview || hasVideoIntro || trip.highlights?.length"
      class="mt-4 md:mt-4 w-full print-hidden"
    >
      <LazyHighlightsVideosMapPreview
        :active-itinerary="activeItinerary"
        :itineraries="itineraries"
        :trip="trip"
        :has-video-intro="hasVideoIntro"
        :has-map-preview="hasMapPreview"
        @open-map="openMap"
      />
    </div>
    <div
      v-if="!!trip.departure_info"
      class="mt-4 md:mt-4 my-print-0 w-full"
    >
      <LazyDepartureInfo :trip="trip" />
    </div>
    <button
      v-if="hasVideoIntro && hasMapPreview"
      class="p-0 details-map-preview overflow-hidden mt-4 md:mt-4 w-full print-hidden relative"
    >
      <LazyMarkedMapPreview
        class="size-full absolute top-0"
        :trip="trip"
        :active-itinerary="activeItinerary"
        @open-map="openMap"
      />
    </button>
    <LazyItinerary
      class="w-full"
      :active-itinerary="activeItinerary"
      :active-itinerary-index="activeItineraryIndex"
      :itineraries="itineraries"
      :active-marker="activeMarker"
      :is-destination="isDestination"
      @set-marker="setMarker"
      @open-map="openMap"
      @select-itinerary="setActiveItinerary"
    />
    <TravelerPictures
      class="w-full mt-4 print-hidden"
      :trip-type-id="trip.id"
      size="500x500"
      fit="c"
    />
    <div class="mt-4 md:mt-4 w-full">
      <HydrateWhenVisible class="min-h-[300px] md:min-h-[200px]">
        <LazyOtherInfo :trip="trip" />
      </HydrateWhenVisible>
    </div>
    <div
      v-if="trip.reviews_average"
      class="mt-4 md:mt-4 w-full print-hidden"
    >
      <HydrateWhenVisible class="min-h-[550px] md:min-h-[300px]">
        <LazyReviews
          :trip="trip"
          :latestreviews="latestreviews"
        />
      </HydrateWhenVisible>
    </div>
    <LazyMarkedMapModal
      v-if="mapOpen"
      :trip="trip"
      :map-open="mapOpen"
      :active-itinerary="activeItinerary"
      :active-marker="activeMarker"
      :is-destination="isDestination"
      @set-marker="setMarker"
      @open-map="openMap"
      @close-map="closeMap"
    />
  </div>
</template>

<script setup>
// Define props
const props = defineProps({
  itineraries: {
    type: Array,
    required: true,
  },
  trip: {
    type: Object,
    required: true,
  },
  suggested: {
    type: Array,
    default: () => [],
  },
  calendar: {
    type: Object,
    default: () => ({}),
  },
  isDestination: {
    type: Boolean,
    default: false,
  },
  latestreviews: {
    type: Array,
    default: () => [],
  },
})

// Define state
const activeMarker = ref(null)
const activeItineraryIndex = ref(0)
const mapOpen = ref(false)

// Map state from Pinia
const localeStore = useLocaleStore()

// Computed properties
const activeItinerary = computed(() => {
  return props.itineraries?.[activeItineraryIndex.value] || props.itineraries?.[0]
})

const hasVideoIntro = computed(() => {
  return localeStore.locale !== 'da' && !!props.trip?.videointroduction?.length
})

const hasMapPreview = computed(() => {
  return isValidMap(activeItinerary.value?.overviewmap)
})

const specialVideoIntroductions = computed(() => {
  return props.trip?.special_videointroduction?.map(videoIntroduction => ({
    ...videoIntroduction,
    logo: {
      url: videoIntroduction.cloudinary_logo,
    },
    media: {
      mediatype: 'video',
      type: videoIntroduction.movie_type,
      location: videoIntroduction.movie_id,
      customThumbnail: {
        url: videoIntroduction.cloudinary_image,
      },
    },
  }))
})

// Lifecycle hook
onMounted(() => {
  activeItineraryIndex.value = props.itineraries.findIndex(obj => obj.mainitinerary === true)
})

// Methods
const setMarker = (i) => {
  activeMarker.value = i
}

const openMap = (day) => {
  mapOpen.value = true
  setMarker(day)
}

const closeMap = () => {
  activeMarker.value = null
  mapOpen.value = false
}

const isValidMap = (map) => {
  if (!map) {
    return false
  }

  if (!map.thumb_bounds.lat_center && !map.thumb_bounds.lng_center && !map.thumb_bounds.zoom) {
    return false
  }

  return true
}

const setActiveItinerary = (itineraryIndex) => {
  activeMarker.value = null
  activeItineraryIndex.value = parseInt(itineraryIndex)
}

const openGallery = (images) => {
  // roomImages.value = null
  // if (images && Array.isArray(images)) {
  //   roomImages.value = images
  // }

  showGallery.value = true

  nextTick(() => {
    hgKey.value++
  })
}
</script>

<style lang="scss">
$resource-and-highlights-min-height: 350px;

.details-resource-and-highlights {
  min-height: $resource-and-highlights-min-height;

  &-video {
    h5 {
      font-size: 1.2rem;

      @media (max-width: theme('screens.md')) {
        font-size: 1rem;
      }
    }

    .play-button {
      border: none;
      opacity: .7;
      transition: opacity .3s;
      height: 60px;
      width: 60px;
    }

    &:hover .play-button {
      opacity: 1;
    }
  }

  &-highlights {
    padding: 40px 35px 40px 35px;

    @media (max-width: theme('screens.md')) {
      padding: 20px 25px 20px 25px;
      min-height: 0 !important;
    }

    h5 {
      font-size: 1.2rem;

      @media (max-width: theme('screens.md')) {
        font-size: 1.1rem;
      }
    }

    hr {
      width: 50px;
      opacity: 1;
    }
  }
}

.details-map-preview {
  min-height: $resource-and-highlights-min-height;

  @media print {
    height: 200px !important;
    min-height: unset !important;
  }

  @media (max-width: theme('screens.md')) {
    min-height: 250px;
  }
}
</style>
